/**
 * Billing Store
 * Manages billing state: balance, transactions, packages, auto-charge, invoices
 * CD-291: Balance display
 * CD-292: Transaction history
 * CD-293: Buy credits page
 * CD-294: Payment methods (Stripe Link)
 * CD-295: Auto-charge configuration
 * CD-452: Invoices
 */
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import billingService from '@/services/billingService'

export const useBillingStore = defineStore('billing', () => {
  // State
  const balance = ref(0)
  const packages = ref([])
  const transactions = ref([])
  const transactionsPagination = ref({ page: 1, limit: 20, total: 0, pages: 0 })
  const paymentMethods = ref([])
  const hasDefaultPayment = ref(false)
  // B-12: track payment-methods fetch state separately from the shared
  // `loading` flag. `paymentMethodsFetched` flips to true after the
  // first successful fetch so the BillingView's empty-state can be
  // gated on "fetched AND empty" — preventing a flash of "No payment
  // methods saved" while the request is still in-flight.
  const loadingPaymentMethods = ref(false)
  const paymentMethodsFetched = ref(false)
  const autoChargeConfig = ref({
    enabled: false,
    package_id: null,
    threshold: 0
  })
  const invoices = ref([])
  const invoicesPagination = ref({ total: 0, limit: 50, offset: 0, has_more: false })
  const loading = ref(false)
  const error = ref(null)
  const userCurrency = ref('GBP')
  const exchangeRate = ref(1)

  // Computed
  const lowBalance = computed(() => balance.value <= 5)
  const zeroBalance = computed(() => balance.value === 0)
  const formattedBalance = computed(() => `${balance.value} credit${balance.value !== 1 ? 's' : ''}`)
  const defaultPaymentMethod = computed(() => paymentMethods.value.find(pm => pm.is_default))
  const sortedPackages = computed(() => [...packages.value].sort((a, b) => a.credits - b.credits))

  // Actions

  /**
   * Fetch current balance
   */
  async function fetchBalance() {
    try {
      const response = await billingService.getBalance()
      balance.value = response.balance
      return response
    } catch (err) {
      error.value = err.detail || 'Failed to fetch balance'
      throw err
    }
  }

  /**
   * CD-566 (retest): set the balance directly, e.g. from the dashboard realtime
   * SSE, so the shared header credit display (DefaultLayout reads
   * billingStore.balance) stays consistent with live balance changes without a
   * refetch or page reload. Previously the header only updated on fetchBalance(),
   * so it diverged from the live Dashboard "Credit Balance" widget.
   */
  function setBalance(value) {
    if (typeof value === 'number' && !Number.isNaN(value)) {
      balance.value = value
    }
  }

  /**
   * Fetch credit packages
   */
  async function fetchPackages() {
    loading.value = true
    try {
      const response = await billingService.getPackages()
      packages.value = response.packages
      userCurrency.value = response.user_currency || 'GBP'
      exchangeRate.value = response.exchange_rate || 1
      return response
    } catch (err) {
      error.value = err.detail || 'Failed to fetch packages'
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Fetch transaction history
   * @param {object} params - Query parameters
   */
  async function fetchTransactions(params = {}) {
    loading.value = true
    try {
      const response = await billingService.getTransactions({
        page: params.page || 1,
        limit: params.limit || 20,
        type: params.type
      })
      transactions.value = response.transactions
      transactionsPagination.value = response.pagination
      return response
    } catch (err) {
      error.value = err.detail || 'Failed to fetch transactions'
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Purchase credits via checkout
   * @param {number} packageId - Package to purchase
   */
  async function purchaseCredits(packageId) {
    loading.value = true
    try {
      const response = await billingService.createCheckoutSession(packageId)
      // Redirect to Stripe Checkout
      window.location.href = response.checkout_url
      return response
    } catch (err) {
      error.value = err.detail || 'Failed to create checkout session'
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Verify checkout session after return
   * @param {string} sessionId - Stripe session ID
   */
  async function verifyCheckout(sessionId) {
    loading.value = true
    try {
      const response = await billingService.verifyCheckoutSession(sessionId)
      if (response.status === 'complete') {
        balance.value = response.new_balance
      }
      return response
    } catch (err) {
      error.value = err.detail || 'Failed to verify checkout'
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Fetch payment methods
   */
  async function fetchPaymentMethods() {
    loading.value = true
    loadingPaymentMethods.value = true
    try {
      const response = await billingService.getPaymentMethods()
      paymentMethods.value = response.payment_methods
      // Use API field if present, otherwise compute from methods list
      hasDefaultPayment.value = response.has_default ?? paymentMethods.value.some(pm => pm.is_default)
      paymentMethodsFetched.value = true
      return response
    } catch (err) {
      error.value = err.detail || 'Failed to fetch payment methods'
      throw err
    } finally {
      loading.value = false
      loadingPaymentMethods.value = false
    }
  }

  /**
   * Set default payment method
   * @param {string} paymentMethodId - Payment method ID
   */
  async function setDefaultPayment(paymentMethodId) {
    loading.value = true
    try {
      await billingService.setDefaultPaymentMethod(paymentMethodId)
      // Update local state
      paymentMethods.value = paymentMethods.value.map(pm => ({
        ...pm,
        is_default: pm.id === paymentMethodId
      }))
      hasDefaultPayment.value = true
      return true
    } catch (err) {
      error.value = err.detail || 'Failed to set default payment method'
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Delete payment method
   * @param {string} paymentMethodId - Payment method ID
   */
  async function deletePaymentMethod(paymentMethodId) {
    loading.value = true
    try {
      await billingService.deletePaymentMethod(paymentMethodId)
      paymentMethods.value = paymentMethods.value.filter(pm => pm.id !== paymentMethodId)
      if (paymentMethods.value.length === 0) {
        hasDefaultPayment.value = false
      }
      return true
    } catch (err) {
      error.value = err.detail || 'Failed to delete payment method'
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Fetch auto-charge configuration
   */
  async function fetchAutoChargeConfig() {
    try {
      const response = await billingService.getAutoChargeConfig()
      autoChargeConfig.value = response
      return response
    } catch (err) {
      error.value = err.detail || 'Failed to fetch auto-charge config'
      throw err
    }
  }

  /**
   * Update auto-charge configuration
   * @param {object} config - Auto-charge settings
   */
  async function updateAutoCharge(config) {
    loading.value = true
    try {
      await billingService.updateAutoCharge(config)
      autoChargeConfig.value = config
      return true
    } catch (err) {
      error.value = err.detail || 'Failed to update auto-charge config'
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Fetch invoices
   * @param {object} params - Query parameters
   */
  async function fetchInvoices(params = {}) {
    loading.value = true
    try {
      const response = await billingService.getInvoices({
        limit: params.limit || 50,
        offset: params.offset || 0
      })
      invoices.value = response.invoices
      invoicesPagination.value = response.pagination
      return response
    } catch (err) {
      error.value = err.detail || 'Failed to fetch invoices'
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Download invoice PDF
   * @param {number} invoiceId - Invoice ID
   * @param {string} invoiceNumber - Invoice number for filename
   */
  async function downloadInvoice(invoiceId, invoiceNumber) {
    try {
      const blob = await billingService.downloadInvoice(invoiceId)
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `${invoiceNumber}.pdf`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (err) {
      error.value = err.detail || 'Failed to download invoice'
      throw err
    }
  }

  /**
   * Clear error
   */
  function clearError() {
    error.value = null
  }

  /**
   * Reset store state
   */
  function $reset() {
    balance.value = 0
    packages.value = []
    transactions.value = []
    transactionsPagination.value = { page: 1, limit: 20, total: 0, pages: 0 }
    invoices.value = []
    invoicesPagination.value = { total: 0, limit: 50, offset: 0, has_more: false }
    paymentMethods.value = []
    hasDefaultPayment.value = false
    autoChargeConfig.value = { enabled: false, package_id: null, threshold: 0 }
    loading.value = false
    error.value = null
    userCurrency.value = 'GBP'
    exchangeRate.value = 1
  }

  return {
    // State
    balance,
    setBalance,
    packages,
    transactions,
    transactionsPagination,
    invoices,
    invoicesPagination,
    paymentMethods,
    hasDefaultPayment,
    loadingPaymentMethods,
    paymentMethodsFetched,
    autoChargeConfig,
    loading,
    error,
    userCurrency,
    exchangeRate,

    // Computed
    lowBalance,
    zeroBalance,
    formattedBalance,
    defaultPaymentMethod,
    sortedPackages,

    // Actions
    fetchBalance,
    fetchPackages,
    fetchTransactions,
    purchaseCredits,
    verifyCheckout,
    fetchPaymentMethods,
    setDefaultPayment,
    deletePaymentMethod,
    fetchAutoChargeConfig,
    updateAutoCharge,
    fetchInvoices,
    downloadInvoice,
    clearError,
    $reset
  }
})
